/**
 * Community Forum Module for Academy Students
 * Discussion boards, Q&A, and student interaction
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  MessageCircle, HelpCircle, BookOpen, Trophy, 
  Plus, Search, ThumbsUp, MessageSquare, Eye,
  CheckCircle, Clock, User, ChevronDown, ChevronUp,
  Send, X, Filter, TrendingUp, Award, Heart,
  AlertCircle, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Category configuration
const CATEGORIES = [
  { id: 'discussion', label: 'Discussion', icon: MessageCircle, color: '#D4AF37' },
  { id: 'question', label: 'Questions', icon: HelpCircle, color: '#3B82F6' },
  { id: 'resource', label: 'Resources', icon: BookOpen, color: '#10B981' },
  { id: 'success-story', label: 'Success Stories', icon: Trophy, color: '#8B5CF6' }
];

// Post Card Component
const PostCard = ({ post, onClick }) => {
  const category = CATEGORIES.find(c => c.id === post.category) || CATEGORIES[0];
  const CategoryIcon = category.icon;
  
  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-[#0A0A0A] border border-white/10 p-4 hover:border-[#D4AF37]/30 transition-all group"
      data-testid={`post-card-${post.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Category Icon */}
        <div 
          className="w-10 h-10 flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${category.color}20` }}
        >
          <CategoryIcon className="w-5 h-5" style={{ color: category.color }} />
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="flex items-center gap-2 mb-1">
            {post.is_pinned && (
              <span className="px-2 py-0.5 bg-[#D4AF37] text-black text-xs font-bold">PINNED</span>
            )}
            {post.category === 'question' && post.is_answered && (
              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-bold flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                SOLVED
              </span>
            )}
            <h3 className="text-white font-medium truncate group-hover:text-[#D4AF37] transition-colors">
              {post.title}
            </h3>
          </div>
          
          {/* Content Preview */}
          <p className="text-white/50 text-sm line-clamp-2 mb-2">
            {post.content}
          </p>
          
          {/* Meta */}
          <div className="flex items-center gap-4 text-xs text-white/40">
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {post.user_name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeAgo(post.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              {post.reply_count} replies
            </span>
            <span className="flex items-center gap-1">
              <ThumbsUp className="w-3 h-3" />
              {post.reaction_count}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {post.view_count} views
            </span>
          </div>
          
          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex gap-1 mt-2 flex-wrap">
              {post.tags.slice(0, 3).map((tag, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-white/5 text-white/50 text-xs">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// Create Post Modal
const CreatePostModal = ({ userId, userName, userEmail, onClose, onSuccess }) => {
  const [category, setCategory] = useState('discussion');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/community/posts`, {
        user_id: userId,
        user_name: userName,
        user_email: userEmail,
        category,
        title: title.trim(),
        content: content.trim(),
        tags: tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
      });
      
      if (response.data.success) {
        onSuccess(response.data.post);
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-post-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-[#0D0D0D] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-white font-bold text-lg">Create New Post</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Category Selection */}
          <div>
            <label className="text-white/50 text-sm mb-2 block">Category</label>
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    className={`flex items-center gap-2 px-3 py-2 border transition-all ${
                      category === cat.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white'
                        : 'border-white/10 text-white/60 hover:border-white/30'
                    }`}
                  >
                    <Icon className="w-4 h-4" style={{ color: cat.color }} />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Title */}
          <div>
            <label className="text-white/50 text-sm mb-2 block">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={category === 'question' ? "What's your question?" : "Post title"}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none"
              data-testid="post-title-input"
            />
          </div>
          
          {/* Content */}
          <div>
            <label className="text-white/50 text-sm mb-2 block">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share your thoughts, ask a question, or post a resource..."
              rows={6}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none resize-none"
              data-testid="post-content-input"
            />
          </div>
          
          {/* Tags */}
          <div>
            <label className="text-white/50 text-sm mb-2 block">Tags (comma separated)</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="trucking, dispatch, compliance..."
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
        </form>
        
        <div className="p-4 border-t border-white/10 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-white/50 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !title.trim() || !content.trim()}
            className="px-6 py-2 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="submit-post-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Post
          </button>
        </div>
      </div>
    </div>
  );
};

// Post Detail View
const PostDetailView = ({ postId, userId, userName, userEmail, onBack }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [reacted, setReacted] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/community/posts/${postId}`);
      setPost(response.data);
    } catch (err) {
      console.error('Failed to fetch post:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    
    setSubmittingReply(true);
    try {
      await axios.post(`${API}/community/posts/${postId}/replies`, {
        user_id: userId,
        user_name: userName,
        user_email: userEmail,
        content: replyContent.trim()
      });
      setReplyContent('');
      fetchPost(); // Refresh to show new reply
    } catch (err) {
      console.error('Failed to post reply:', err);
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleReact = async () => {
    try {
      const response = await axios.post(`${API}/community/posts/${postId}/react`, {
        user_id: userId,
        reaction_type: 'like'
      });
      setReacted(response.data.action === 'added');
      fetchPost(); // Refresh to update count
    } catch (err) {
      console.error('Failed to react:', err);
    }
  };

  const handleAcceptAnswer = async (replyId) => {
    try {
      await axios.post(`${API}/community/replies/${replyId}/accept?user_id=${userId}`);
      fetchPost();
    } catch (err) {
      console.error('Failed to accept answer:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="text-center py-12 text-white/50">
        Post not found
      </div>
    );
  }

  const category = CATEGORIES.find(c => c.id === post.category) || CATEGORIES[0];
  const CategoryIcon = category.icon;
  const isAuthor = post.user_id === userId;

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="space-y-4" data-testid="post-detail-view">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-[#D4AF37] hover:underline text-sm"
      >
        ← Back to Forum
      </button>
      
      {/* Post Header */}
      <div className="bg-[#0A0A0A] border border-white/10 p-6">
        <div className="flex items-start gap-4">
          <div 
            className="w-12 h-12 flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${category.color}20` }}
          >
            <CategoryIcon className="w-6 h-6" style={{ color: category.color }} />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span 
                className="px-2 py-0.5 text-xs font-bold"
                style={{ backgroundColor: `${category.color}20`, color: category.color }}
              >
                {category.label.toUpperCase()}
              </span>
              {post.is_answered && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-bold flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  SOLVED
                </span>
              )}
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-3">{post.title}</h1>
            
            <div className="flex items-center gap-4 text-sm text-white/50 mb-4">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.user_name}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {timeAgo(post.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {post.view_count} views
              </span>
            </div>
            
            <div className="text-white/80 whitespace-pre-wrap leading-relaxed">
              {post.content}
            </div>
            
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex gap-2 mt-4 flex-wrap">
                {post.tags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white/5 text-white/50 text-sm">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
            
            {/* Actions */}
            <div className="flex items-center gap-4 mt-6 pt-4 border-t border-white/10">
              <button
                onClick={handleReact}
                className={`flex items-center gap-2 px-4 py-2 border transition-colors ${
                  reacted 
                    ? 'bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]'
                    : 'border-white/10 text-white/60 hover:border-white/30 hover:text-white'
                }`}
              >
                <Heart className={`w-4 h-4 ${reacted ? 'fill-current' : ''}`} />
                {post.reaction_count} Likes
              </button>
              <span className="text-white/40 text-sm">
                {post.reply_count} {post.reply_count === 1 ? 'reply' : 'replies'}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Replies */}
      <div className="space-y-3">
        <h3 className="text-white font-bold">Replies ({post.replies?.length || 0})</h3>
        
        {post.replies?.map((reply) => (
          <div 
            key={reply.id}
            className={`bg-[#0A0A0A] border p-4 ${
              reply.is_accepted_answer 
                ? 'border-green-500/50 bg-green-500/5' 
                : 'border-white/10'
            }`}
          >
            {reply.is_accepted_answer && (
              <div className="flex items-center gap-2 text-green-400 text-sm font-bold mb-2">
                <CheckCircle className="w-4 h-4" />
                Accepted Answer
              </div>
            )}
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-white/10 flex items-center justify-center text-white/50 text-sm font-bold flex-shrink-0">
                {reply.user_name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-medium text-sm">{reply.user_name}</span>
                  <span className="text-white/40 text-xs">{timeAgo(reply.created_at)}</span>
                </div>
                <p className="text-white/80 text-sm whitespace-pre-wrap">{reply.content}</p>
                
                {/* Accept Answer Button (only for question author) */}
                {isAuthor && post.category === 'question' && !post.is_answered && (
                  <button
                    onClick={() => handleAcceptAnswer(reply.id)}
                    className="mt-2 text-green-400 text-xs flex items-center gap-1 hover:underline"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Mark as Answer
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {(!post.replies || post.replies.length === 0) && (
          <div className="text-center py-8 text-white/40 text-sm">
            No replies yet. Be the first to respond!
          </div>
        )}
      </div>
      
      {/* Reply Form */}
      <form onSubmit={handleReply} className="bg-[#0A0A0A] border border-white/10 p-4">
        <h3 className="text-white font-bold mb-3">Add a Reply</h3>
        <textarea
          value={replyContent}
          onChange={(e) => setReplyContent(e.target.value)}
          placeholder="Share your thoughts or answer..."
          rows={3}
          className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none resize-none mb-3"
          data-testid="reply-input"
        />
        <button
          type="submit"
          disabled={submittingReply || !replyContent.trim()}
          className="px-6 py-2 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="submit-reply-btn"
        >
          {submittingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Reply
        </button>
      </form>
    </div>
  );
};

// Main Community Module Component
const CommunityModule = ({ userId, userName, userEmail }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [stats, setStats] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCategory) params.append('category', activeCategory);
      if (searchQuery) params.append('search', searchQuery);
      params.append('sort_by', sortBy);
      
      const response = await axios.get(`${API}/community/posts?${params}`);
      setPosts(response.data.posts || []);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, searchQuery, sortBy]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/community/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchStats();
  }, [fetchPosts]);

  const handlePostCreated = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
  };

  // If viewing a specific post
  if (selectedPostId) {
    return (
      <div className="p-4 md:p-6" data-testid="community-module">
        <PostDetailView 
          postId={selectedPostId}
          userId={userId}
          userName={userName}
          userEmail={userEmail}
          onBack={() => setSelectedPostId(null)}
        />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="community-module">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Community Forum</h2>
          <p className="text-white/50 text-sm">Connect with fellow students, ask questions, and share knowledge</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] transition-colors"
          data-testid="create-post-btn"
        >
          <Plus className="w-5 h-5" />
          New Post
        </button>
      </div>
      
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#0A0A0A] border border-white/10 p-4 text-center">
            <div className="text-2xl font-bold text-[#D4AF37]">{stats.total_posts}</div>
            <div className="text-white/50 text-xs">Posts</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-4 text-center">
            <div className="text-2xl font-bold text-[#D4AF37]">{stats.total_replies}</div>
            <div className="text-white/50 text-xs">Replies</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-4 text-center">
            <div className="text-2xl font-bold text-green-400">{stats.answer_rate}%</div>
            <div className="text-white/50 text-xs">Questions Answered</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-4 text-center">
            <div className="text-2xl font-bold text-purple-400">{stats.top_contributors?.length || 0}</div>
            <div className="text-white/50 text-xs">Active Members</div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search posts..."
            className="w-full bg-white/5 border border-white/10 pl-10 pr-4 py-2 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none"
            data-testid="search-input"
          />
        </div>
        
        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="bg-white/5 border border-white/10 px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
        >
          <option value="recent">Most Recent</option>
          <option value="popular">Most Popular</option>
          <option value="unanswered">Unanswered</option>
        </select>
      </div>
      
      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveCategory(null)}
          className={`flex items-center gap-2 px-4 py-2 whitespace-nowrap transition-colors ${
            activeCategory === null
              ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-white'
              : 'bg-white/5 border border-white/10 text-white/60 hover:border-white/30'
          }`}
        >
          All Posts
        </button>
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 whitespace-nowrap transition-colors ${
                activeCategory === cat.id
                  ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-white'
                  : 'bg-white/5 border border-white/10 text-white/60 hover:border-white/30'
              }`}
            >
              <Icon className="w-4 h-4" style={{ color: cat.color }} />
              {cat.label}
            </button>
          );
        })}
      </div>
      
      {/* Posts List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <MessageCircle className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h3 className="text-white font-bold text-lg mb-2">No posts yet</h3>
          <p className="text-white/50 text-sm mb-4">Be the first to start a conversation!</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] transition-colors"
          >
            Create First Post
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <PostCard 
              key={post.id} 
              post={post} 
              onClick={() => setSelectedPostId(post.id)}
            />
          ))}
        </div>
      )}
      
      {/* Create Post Modal */}
      {showCreateModal && (
        <CreatePostModal
          userId={userId}
          userName={userName}
          userEmail={userEmail}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handlePostCreated}
        />
      )}
    </div>
  );
};

export default CommunityModule;
